import { useCallback } from 'react';
import { useReactFlow, getNodesBounds, getViewportForBounds } from '@xyflow/react';
import { toPng, toSvg } from 'html-to-image';
import { jsPDF } from 'jspdf';
import type { Flujograma } from '../types/flowchart.types';

function getExportElement(
  containerRef: React.RefObject<HTMLDivElement | null>,
): HTMLElement | null {
  if (!containerRef.current) return null;
  const viewport = containerRef.current.querySelector('.react-flow__viewport') as HTMLElement;
  if (viewport) return viewport;
  return containerRef.current.querySelector('.react-flow') as HTMLElement;
}

function exportFilter(node: HTMLElement): boolean {
  const cn = node.className?.toString() ?? '';
  return (
    !cn.includes('react-flow__minimap') &&
    !cn.includes('react-flow__controls') &&
    !cn.includes('react-flow__panel')
  );
}

interface UseFlowExportOptions {
  containerRef: React.RefObject<HTMLDivElement | null>;
  flujograma: Flujograma | null;
  procesoId: string;
}

export function useFlowExport({ containerRef, flujograma, procesoId }: UseFlowExportOptions) {
  const { getNodes } = useReactFlow();

  const getExportFilename = useCallback(
    (ext: string) => {
      const code = flujograma?.procesoId ?? procesoId;
      const version = flujograma?.version ?? 1;
      return `${code}_v${version}.${ext}`;
    },
    [flujograma, procesoId],
  );

  const handleExportPng = useCallback(async () => {
    const element = getExportElement(containerRef);
    if (!element) return;
    try {
      const currentNodes = getNodes();
      if (currentNodes.length > 0) {
        const bounds = getNodesBounds(currentNodes);
        void getViewportForBounds(bounds, bounds.width + 100, bounds.height + 100, 0.5, 2, 0.1);
      }
      const dataUrl = await toPng(element, {
        backgroundColor: '#fff',
        pixelRatio: 2,
        filter: exportFilter,
      });
      const link = document.createElement('a');
      link.download = getExportFilename('png');
      link.href = dataUrl;
      link.click();
    } catch {
      // Export failed silently
    }
  }, [containerRef, getNodes, getExportFilename]);

  const handleExportPdf = useCallback(async () => {
    const element = getExportElement(containerRef);
    if (!element) return;
    try {
      const dataUrl = await toPng(element, {
        backgroundColor: '#fff',
        pixelRatio: 2,
        filter: exportFilter,
      });
      const img = new Image();
      img.src = dataUrl;
      await new Promise<void>((resolve) => {
        img.onload = () => resolve();
      });

      const pageW = 842;
      const pageH = 595;
      const headerH = 40;
      const footerH = 24;
      const contentH = pageH - headerH - footerH - 20;
      const contentW = pageW - 40;

      const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [pageW, pageH] });

      pdf.setFillColor(31, 56, 100);
      pdf.rect(0, 0, pageW, headerH, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.setTextColor(255, 255, 255);
      const code = flujograma?.procesoId ?? procesoId;
      const version = flujograma?.version ?? 1;
      const headerText = `${code} \u2014 Ed. ${String(version).padStart(2, '0')}`;
      pdf.text(headerText, 20, 26);

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      const dateStr = new Date().toLocaleDateString();
      pdf.text(dateStr, pageW - 20, 26, { align: 'right' });

      const scale = Math.min(contentW / img.width, contentH / img.height);
      const imgW = img.width * scale;
      const imgH = img.height * scale;
      const imgX = (pageW - imgW) / 2;
      const imgY = headerH + 10;
      pdf.addImage(dataUrl, 'PNG', imgX, imgY, imgW, imgH);

      pdf.setFontSize(7);
      pdf.setTextColor(140, 148, 160);
      pdf.text('Generado por V\u00b7IANet Governance', pageW / 2, pageH - 10, { align: 'center' });

      pdf.save(getExportFilename('pdf'));
    } catch {
      // Export failed silently
    }
  }, [containerRef, flujograma, procesoId, getExportFilename]);

  const handleExportSvg = useCallback(async () => {
    const element = getExportElement(containerRef);
    if (!element) return;
    try {
      const dataUrl = await toSvg(element, {
        backgroundColor: '#fff',
        filter: exportFilter,
      });
      const link = document.createElement('a');
      link.download = getExportFilename('svg');
      link.href = dataUrl;
      link.click();
    } catch {
      // Export failed silently
    }
  }, [containerRef, getExportFilename]);

  return { handleExportPng, handleExportPdf, handleExportSvg };
}
